import { Button } from 'reactstrap';
import { DocumentMetadata } from '../../../../api/ai-supervisor';
import { Answer } from '../../types';
import { ScopeQuestionOptionalValue } from '../../../../types/survey';
import { MarkdownRenderer } from '../MarkdownRenderer';

interface ExpansionContentProps {
  question: ScopeQuestionOptionalValue;
  answer?: Answer;
  documentMetadata?: Record<string, DocumentMetadata>;
  handleApprove: (id: string) => void;
  setSelectedAnswer: (answer: Answer) => void;
}

export const ExpansionContent = (props: ExpansionContentProps) => {
  const { question, answer, documentMetadata, handleApprove, setSelectedAnswer } = props;
  if (!answer) {
    return (
      <div className='px-4 py-3'>
        <p className='text-muted mb-0'>No answer available for this question.</p>
      </div>
    );
  }

  const needsApproval = Boolean(
    answer.status === 'completed' &&
    answer.confidence &&
      answer.confidence < 90 &&
      !answer.approved,
  );
  const chunks = answer.retrievedChunks || [];

  return (
    <div className='px-4 py-3'>
      <div className='ms-4'>
        <div className='mb-3'>
          <h4 className='small text-muted mb-2'>Question</h4>
          <p className='small text-dark mb-0'>{question.universalTracker.getValueLabel()}</p>
        </div>

        <div className='mb-3'>
          <h4 className='small text-muted mb-2'>Answer</h4>
          <div className='small text-dark mb-0 answer-content'>
            {answer.answer ? (typeof answer.answer === 'string' ? <MarkdownRenderer content={answer.answer} /> : <div>{answer.answer}</div>) : 'No answer available'}
          </div>
        </div>

        {answer.citationsCount > 0 && (
          <div className='mb-3'>
            <h4 className='small text-muted mb-2'>Sources</h4>
            <div className='d-flex flex-wrap gap-2'>
              {[...chunks].sort((a, b) => a.chunkIndex - b.chunkIndex).map((chunk, idx) => {
                // Look up document name from metadata
                const docMetadata = documentMetadata?.[chunk.externalDocumentId];
                const displayName = docMetadata?.fileName || `Document ${chunk.externalDocumentId}`;

                return (
                  <div
                    key={idx}
                    className='d-flex align-items-center gap-2 px-3 py-2 rounded bg-white border small'
                  >
                    <i className='fal fa-file-alt text-primary' />
                    <span className='text-dark text-truncate' style={{ maxWidth: '100px' }} title={displayName}>
                      {displayName}
                    </span>
                    {chunk.pageStart && <span className='text-muted'>• page {chunk.pageStart}</span>}
                    {chunk.chunkIndex !== undefined && (
                      <span className='text-muted'>• chunk {chunk.chunkIndex + 1}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className='border-top border-ThemeNeutralsLight my-3' />

        <div className='d-flex align-items-center justify-content-between mt-3'>
          <div className='d-flex align-items-center gap-2'>
            {needsApproval && (
              <div className='d-flex align-items-center gap-2 small text-warning'>
                <i className='fal fa-exclamation-triangle' />
                <span>Confidence below 90% - Please review and approve</span>
              </div>
            )}
          </div>
          <div className='d-flex align-items-center gap-2'>
            {needsApproval && (
              <Button
                size='sm'
                onClick={() => handleApprove(answer.id)}
                className='bg-success text-white border-0'
              >
                <i className='fal fa-thumbs-up me-2' />
                Approve Answer
              </Button>
            )}
            <Button outline size='sm' onClick={() => setSelectedAnswer(answer)}>
              View Details
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
